import { MouseEventHandler, useMemo, useRef, useState } from "react"
import { CalendarCore, DateData } from "./core"
import { SCalendar } from "./style"

type Props = {
  selectedDate?: string,
  dayIndicatorSet?: Set<string>,
  hasToday?: boolean,
  hasClose?: boolean,
  isRtl?: boolean,
  onEvent?: (e: {
    component: "Calendar",
    type: string,
    data: Record<string, any> | null,
  }) => any,
}
type StateRef = {
  domain: "year" | "month" | "day",
  locationDateData: DateData,
  selectedDateData: DateData | null,
}

export const Calendar = ({
  selectedDate,
  dayIndicatorSet,
  hasToday,
  hasClose,
  isRtl,
  onEvent,
}: Props) => {

  const
  core = useRef(new CalendarCore()).current,
  ref = useRef({
    yearsLength: 12,
    charDays: ["일", "월", "화", "수", "목", "금", "토"],
    domains: ["year", "month", "day"],
    event: {
      component: "Calendar",
      type: "",
      data: null,
    } as const,
    isRtl: !!isRtl,
    onEvent: typeof onEvent == "function" ? onEvent : null,
  }).current,
  stateRef = useRef<StateRef>({
    domain: "day",
    locationDateData: core.getDateData(core.todayValues.dateStr)!,
    selectedDateData: null,
  }).current,
  _ = useMemo(() => {
    const validation = core.isValidDate(selectedDate)
    if (validation.isValid) {
      stateRef.locationDateData
      = stateRef.selectedDateData
      = core.getDateData(validation.dateIns)!
    }
  }, [selectedDate]),
  [rerendered, rerender] = useState(true),
  {
    domain,
    isYear,
    yearCalendar,
    isMonth,
    monthCalendar,
    isDay,
    dayCalendar,
    className,
  } = useMemo(() => {
    switch (stateRef.domain) {
      case "year": {
        return {
          domain: "year",
          isYear: true,
          yearCalendar: core.getYearCalendarData(stateRef.locationDateData.dateValues.year),
          className: "Calendar year"
        }
      }
      case "month": {
        return {
          domain: "month",
          isMonth: true,
          monthCalendar: core.getMonthCalendarData(stateRef.locationDateData.dateValues.year),
          className: "Calendar month",
        }
      }
      default: {
        return {
          domain: "day",
          isDay: true,
          dayCalendar: core.getDayCalendarData(stateRef.locationDateData.dateIns),
          className: "Calendar day",
        }
      }
    }
  }, [selectedDate, rerendered])

  ref.onEvent = typeof onEvent == "function" ? onEvent : null
  ref.isRtl = !!isRtl

  const onClick: MouseEventHandler = e => {
    try {
      const {target} = e
      if (target instanceof HTMLElement) {
        const
        {dataset} = target,
        {onclick} = dataset
        if (onclick) {
          switch (onclick) {
            case "calc": {
              switch (dataset.calc) {
                case "--":
                case "++": {
                  const
                  isPrev = dataset.calc == "--",
                  sign = isPrev ? -1 : 1,
                  {dateIns, dateValues} = stateRef.locationDateData,
                  diff = sign * (isYear ? ref.yearsLength : 1),
                  newYear = dateValues.year + diff
                  if (newYear <= 0) return
                  dateIns.setFullYear(newYear, dateIns.getMonth(), 1)
                  stateRef.locationDateData = core.getDateData(dateIns)!
                  break
                }
                case "-": 
                case "+": {
                  const isPrev = dataset.calc == "-",
                  sign = isPrev ? -1 : 1,
                  {dateIns} = stateRef.locationDateData
                  dateIns.setMonth(dateIns.getMonth() + sign, 1)
                  stateRef.locationDateData = core.getDateData(dateIns)!
                  break
                }
                default: return
              }
              break
            }
            case "domain": {
              let newDomain = String(dataset.domain)
              if (core.isDateDomain(newDomain)) {
                stateRef.domain = newDomain == domain ? "day" : newDomain
              }
              break
            }
            case "today": {
              const dateStr = core.todayValues.dateStr
              stateRef.locationDateData = core.getDateData(dateStr)!
              break
            }
            case "close": {
              ref.onEvent?.({
                ...ref.event,
                type: "close"
              })
              return
            }
            case "year": {
              const newLocationYear = Number.parseInt(dataset.year ?? "")
              if (newLocationYear < 1) return
              const locationDateIns = stateRef.locationDateData.dateIns
              locationDateIns.setFullYear(newLocationYear, locationDateIns.getMonth(), 1)
              stateRef.locationDateData = core.getDateData(locationDateIns)!
              stateRef.domain = "month"
              break
            }
            case "month": {
              const newLocationMonth = Number.parseInt(dataset.month ?? "")
              if (newLocationMonth < 1) return
              const locationDateIns = stateRef.locationDateData.dateIns
              locationDateIns.setMonth(newLocationMonth - 1, 1)
              stateRef.locationDateData = core.getDateData(locationDateIns)!
              stateRef.domain = "day"
              break
            }
            case "day": {
              const dateStr = String(dataset.date)
              if (stateRef.selectedDateData?.dateValues.dateStr == dateStr) return
              const newSelectedDateValues = core.getDateData(dateStr)!.dateValues
              ref.onEvent?.({
                ...ref.event,
                data: {
                  domain: stateRef.domain,
                  locationDateValues: {...stateRef.locationDateData.dateValues},
                  selectedDateValues: newSelectedDateValues,
                }
              })
              return
            }
            default: return
          }
          ref.onEvent?.({
            ...ref.event,
            data: {
              domain: stateRef.domain,
              locationDateValues: {...stateRef.locationDateData.dateValues},
              selectedDateValues: stateRef.selectedDateData ? {...stateRef.selectedDateData.dateValues} : null,
            }
          })
          rerender(prev => !prev)
        }
      }
    } catch (e) {
      console.error(`Calendar.onClick > `, e)
    }
  }

  return (
    <SCalendar className={className} onClick={onClick}>
      <header>
        <div className="front">
          <button className="calc rotate" data-onclick="calc" data-calc="--"><Svg_Arrow2 /></button>
          <button className="calc rotate" data-onclick="calc" data-calc="-"><Svg_Arrow /></button>
          <div className="domains">
            <button className="domain year" data-onclick="domain" data-domain="year">{stateRef.locationDateData.dateValues.yearStr}</button>
            /
            <button className="domain month" data-onclick="domain" data-domain="month">{stateRef.locationDateData.dateValues.monthStr}</button>
          </div>
          <button className="calc" data-onclick="calc" data-calc="+"><Svg_Arrow /></button>
          <button className="calc" data-onclick="calc" data-calc="++"><Svg_Arrow2 /></button>
        </div>
        <div className="back">
          {hasToday && <button data-onclick="today">Today</button>}
          {hasClose && <button data-onclick="close">닫기</button>}
        </div>
      </header>

      {isDay && (
        <aside className="charDays">
          {ref.charDays.map(item => <span key={item}>{item}</span>)}
        </aside>
      )}

      <menu>
        {yearCalendar?.list.map(item => {
          const dateValues = stateRef.selectedDateData?.dateValues
          const isActive = item.year == dateValues?.year
          return (
            <button
              key={item.year}
              className={isActive ? "active" : ""}
              data-onclick="year"
              data-year={item.yearStr}>
              {item.year}
            </button>
          )
        }) ?? monthCalendar?.list.map(item => {
          
          const dateValues = stateRef.selectedDateData?.dateValues
          const isActive = item.calendarKey == dateValues?.calendarKey
          return (
            <button
              key={item.calendarKey}
              className={isActive ? "active" : ""}
              data-onclick="month"
              data-month={item.monthStr}>
              {item.month}
            </button>
          )
        }) ?? dayCalendar?.list.map(item => {
          const dateValues = stateRef.selectedDateData?.dateValues
          const isActive = item.dateStr == dateValues?.dateStr
          const isCurr = item.domain == "curr"
          const hasIndicator = dayIndicatorSet?.has(item.dateStr)
          const isToday = item.dateStr == core.todayValues.dateStr
          const className = [
            isActive ? "active" : "",
            hasIndicator ? "indicated" : "",
            isToday ? "today" : "",
          ].join(" ")
          return (
            <button
              key={item.dateStr}
              disabled={!isCurr}
              className={className}
              data-onclick="day"
              data-day={item.dayStr}
              data-day-index={item.dayIndex}
              data-date={item.dateStr}>
              {item.day}
            </button>
          )
        }) ?? null}
      </menu>
    </SCalendar>
  )
}

const Svg_Arrow = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <g transform="translate(-1 0.5)" fill="currentColor" stroke="none">
        <path d="M-4517.5 1312a1.5 1.5 0 0 1-1.5-1.5v-5a1.5 1.5 0 0 1 1.5-1.5 1.5 1.5 0 0 1 1.5 1.5v3.5h3.5a1.5 1.5 0 0 1 1.5 1.5 1.5 1.5 0 0 1-1.5 1.5z" transform="rotate(-135 -2522.135 -278.7)" />
      </g>
    </svg>
  )
}

const Svg_Arrow2 = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <g transform="translate(-1 0.5)" fill="currentColor" stroke="none">
        <path d="M-4517.5 1312a1.5 1.5 0 0 1-1.5-1.5v-5a1.5 1.5 0 0 1 1.5-1.5 1.5 1.5 0 0 1 1.5 1.5v3.5h3.5a1.5 1.5 0 0 1 1.5 1.5 1.5 1.5 0 0 1-1.5 1.5z" transform="rotate(-135 -2521.135 -279.115)" />
        <path d="M-4517.5 1312a1.5 1.5 0 0 1-1.5-1.5v-5a1.5 1.5 0 0 1 1.5-1.5 1.5 1.5 0 0 1 1.5 1.5v3.5h3.5a1.5 1.5 0 0 1 1.5 1.5 1.5 1.5 0 0 1-1.5 1.5z" transform="rotate(-135 -2523.807 -278.008)" />
      </g>
    </svg>
  )
}

const Svg_X = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="33" height="33" viewBox="0 0 33 33">
      <g fill="currentColor" stroke="none">
        <path d="M9.191 20.353v-7.222H1.969a1.97 1.97 0 1 1 0-3.939h7.222V1.97a1.97 1.97 0 0 1 3.939 0v7.222h7.222a1.97 1.97 0 0 1 0 3.939H13.13v7.222a1.97 1.97 0 1 1-3.939 0z" transform="rotate(45 7.009 20.495)" />
      </g>
    </svg>
  )
}